import { DateTime } from "luxon";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { CalendarTheme } from "@/domains/appointment/appointments/config/calendar-config";
import type { CalendarViewType } from "../types";

interface CalendarStateStore {
  viewType: CalendarViewType;
  currentDate: DateTime;
  timeScale: CalendarTheme;
  statusFilters: string[];
  workerFilters: string[];

  setViewType: (viewType: CalendarViewType) => void;
  setCurrentDate: (date: DateTime) => void;
  setTimeScale: (scale: CalendarTheme) => void;
  setCalendarState: (viewType: CalendarViewType, date: DateTime) => void;
  setStatusFilters: (filters: string[]) => void;
  setWorkerFilters: (filters: string[]) => void;
  clearCalendarFilters: () => void;
}

// Simplified fragment utility functions with backward compatibility
const parseCalendarFragment = (hash: string): { mode: CalendarViewType; date: DateTime } => {
  const fragment = hash.replace("#", "");
  if (!fragment) {
    return {
      mode: "day",
      date: DateTime.now(),
    };
  }

  // Check if it's the old URLSearchParams format (contains = or &)
  if (fragment.includes("=") || fragment.includes("&")) {
    const params = new URLSearchParams(fragment);
    const mode = (params.get("mode") as CalendarViewType) || "day";
    const dateStr = params.get("date");
    const date = dateStr ? DateTime.fromFormat(dateStr, "yyyy-MM-dd") : DateTime.now();
    return { mode, date };
  }

  // New simple format: viewType-YYYY-MM-DD (e.g., "day-2024-01-15", "worker-2024-01-15")
  // Handle special case for "day-worker" (backward compatibility) and "worker" which contain hyphens
  const validViewTypes = ["day", "3-day", "week", "month", "worker", "day-worker"];

  for (const viewType of validViewTypes) {
    if (fragment.startsWith(`${viewType}-`)) {
      const dateStr = fragment.substring(viewType.length + 1); // Remove "viewType-" prefix
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Validate date format YYYY-MM-DD
        const date = DateTime.fromFormat(dateStr, "yyyy-MM-dd");
        // Map "day-worker" to "worker" for backward compatibility
        const mappedViewType = viewType === "day-worker" ? "worker" : viewType;
        return { mode: mappedViewType as CalendarViewType, date };
      }
    }
  }

  return {
    mode: "day",
    date: DateTime.now(),
  };
};

const generateCalendarFragment = (mode: CalendarViewType, date: DateTime): string => {
  const dateStr = date.toFormat("yyyy-MM-dd");
  return `${mode}-${dateStr}`;
};

// Export unified URL generation function for external use
export const getCalendarUrl = (mode: CalendarViewType, date: DateTime): string => {
  const fragment = generateCalendarFragment(mode, date);
  return `/calendar#${fragment}`;
};

// Export fragment parsing for calendar page component
export const parseAndConsumeUrlFragment = (): {
  mode: CalendarViewType;
  date: DateTime;
} | null => {
  if (typeof window !== "undefined" && window.location.hash) {
    const { mode, date } = parseCalendarFragment(window.location.hash);

    // Clear fragment immediately after consuming
    window.history.replaceState(null, "", "/calendar");

    return { mode, date };
  }
  return null;
};

// URL generation removed - store is memory-only after initial load

// Get default initial state (URL parsing happens after store creation)
const getInitialState = (): {
  viewType: CalendarViewType;
  currentDate: DateTime;
  timeScale: CalendarTheme;
  statusFilters: string[];
  workerFilters: string[];
} => {
  return {
    viewType: "day" as CalendarViewType,
    currentDate: DateTime.now(),
    timeScale: "detailed" as CalendarTheme,
    statusFilters: [] as string[],
    workerFilters: [] as string[],
  };
};

export const useCalendarStateStore = create<CalendarStateStore>()(
  devtools(
    persist(
      (set, _get) => {
        // Get initial state from defaults
        const initialState = getInitialState();

        // URL fragment handling moved to calendar-page.tsx component

        return {
          ...initialState,

          setViewType: (viewType) => {
            set({ viewType });
          },

          setCurrentDate: (date) => {
            // Ensure we never store an invalid date
            const validDate = date?.isValid ? date : DateTime.now();

            set({ currentDate: validDate });
          },

          setTimeScale: (timeScale) => {
            set({ timeScale });
          },

          setCalendarState: (viewType, date) => {
            // Ensure we never store an invalid date
            const validDate = date?.isValid ? date : DateTime.now();

            set({ viewType, currentDate: validDate });
          },

          setStatusFilters: (filters) => {
            set({ statusFilters: filters });
          },

          setWorkerFilters: (filters) => {
            set({ workerFilters: filters });
          },

          clearCalendarFilters: () => {
            set({ statusFilters: [], workerFilters: [] });
          },
        };
      },
      {
        name: "calendar-state-storage",
        partialize: (state) => ({
          // viewType is no longer persisted - managed by new calendar store
          timeScale: state.timeScale,
          statusFilters: state.statusFilters,
          workerFilters: state.workerFilters,
        }),
        // No special hydration logic needed - URL parsing handles initial state
      }
    ),
    {
      name: "calendar-state-store",
    }
  )
);
